import { ReactNode, useEffect, useState } from 'react';
import { Mui } from '@onedesign/ui';
import { Icon, SvgName } from 'components/Icon';
import { useSnackbar } from 'hooks/index';
import { t } from 'utils/i18n';
import {
  StyledBackButton,
  StyledDescription,
  StyledErrorHelperText,
  StyledVerificationCodeFormMain,
} from './VerificationCodeForm.style';

const { Button, TextField } = Mui;

export type TVerificationCodeSubmitResult =
  | 'success'
  | 'invalid'
  | 'expired'
  | 'locked';

export interface IVerificationCodeFormProps {
  title: string;
  description: ReactNode;
  /** 重寄按鈕解鎖的絕對時間戳（epoch ms），由外層頁面透過 URL query 控制，重整頁面也能算出正確剩餘秒數 */
  resendAvailableAt: number;
  onBack: () => void;
  onSubmit: (code: string) => Promise<TVerificationCodeSubmitResult>;
  onResend: () => Promise<boolean>;
}

const VerificationCodeForm = ({
  title,
  description,
  resendAvailableAt,
  onBack,
  onSubmit,
  onResend,
}: IVerificationCodeFormProps) => {
  const [code, setCode] = useState('');
  const [errorType, setErrorType] = useState<
    'invalid' | 'expired' | 'locked' | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, forceTick] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const timer = setInterval(() => forceTick(tick => tick + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const remainingSeconds = Math.max(
    0,
    Math.ceil((resendAvailableAt - Date.now()) / 1000),
  );
  const isResendDisabled = remainingSeconds > 0;

  const handleSubmit = async () => {
    if (!code.trim() || isSubmitting) return;
    setIsSubmitting(true);
    const result = await onSubmit(code.trim());
    setIsSubmitting(false);
    setErrorType(result === 'success' ? null : result);
  };

  const handleResend = async () => {
    if (isResendDisabled) return;
    const isSuccess = await onResend();
    if (isSuccess) {
      setErrorType(null);
      setCode('');
    } else {
      enqueueSnackbar(
        t(
          'components.verificationCodeForm.resendFailedMessage',
          '重寄失敗，請稍後再試',
        ),
        { variant: 'error' },
      );
    }
  };

  return (
    <StyledVerificationCodeFormMain>
      <StyledBackButton
        color="inherit"
        startIcon={<Icon name={SvgName.ChevronLeft} />}
        onClick={onBack}
      >
        {t('components.verificationCodeForm.back', '返回')}
      </StyledBackButton>
      <h2>{title}</h2>
      <StyledDescription>{description}</StyledDescription>
      <TextField
        fullWidth
        value={code}
        onChange={e => setCode(e.target.value)}
        placeholder={t(
          'components.verificationCodeForm.codeInputPlaceholder',
          '請輸入驗證碼',
        )}
        error={!!errorType}
      />
      {errorType === 'invalid' && (
        <StyledErrorHelperText>
          {t(
            'components.verificationCodeForm.invalidMessage',
            '驗證碼錯誤，請重新輸入',
          )}
        </StyledErrorHelperText>
      )}
      {errorType === 'expired' && (
        <StyledErrorHelperText>
          {t(
            'components.verificationCodeForm.expiredMessage',
            '驗證碼已過期，請重新發送',
          )}
        </StyledErrorHelperText>
      )}
      {errorType === 'locked' && (
        <StyledErrorHelperText>
          {t(
            'components.verificationCodeForm.lockedMessage',
            '此驗證碼已被鎖定，請重新發送驗證碼',
          )}
        </StyledErrorHelperText>
      )}
      <Button
        variant="contained"
        sx={{ padding: '12px 0' }}
        disabled={!code.trim() || isSubmitting}
        onClick={handleSubmit}
      >
        {t('components.verificationCodeForm.submit', '送出')}
      </Button>
      <Button
        variant="contained"
        color="inherit"
        sx={{ padding: '12px 0' }}
        disabled={isResendDisabled}
        onClick={handleResend}
      >
        {t('components.verificationCodeForm.resend', '重寄驗證碼')}
        {isResendDisabled && ` (${remainingSeconds})`}
      </Button>
    </StyledVerificationCodeFormMain>
  );
};

export default VerificationCodeForm;
